import { Component, computed, inject, input, LOCALE_ID, signal } from '@angular/core';
import { filter, switchMap, tap } from 'rxjs';
import { PharmacyCreditService } from '../../../../services/pharmacy-credit/pharmacy-credit';
import { toObservable } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-hero-section',
  imports: [],
  templateUrl: './hero-section.html',
  styleUrl: './hero-section.scss',
})
export class HeroSection {
  private localeId = inject(LOCALE_ID);
  private pharmacyCreditService = inject(PharmacyCreditService);

  pharmacyId = input<number | null>(null);

  totalCredit = signal<number | null>(null);
  usedCredit = signal<number | null>(null);
  remainingCredit = signal<number | null>(null);
  apiError = signal<string | null>(null);

  isLoading = computed(() => this.pharmacyCreditService.loadingCredit());
  isArabic = computed(() => this.localeId.startsWith('ar'));

  creditPercentage = computed(() => {
    const total = this.totalCredit();
    const used = this.usedCredit();
    if (!total || total === 0 || used == null) return 0;
    return (used / total) * 100;
  });

  progressBarGradient = computed(() => {
    const percentage = this.creditPercentage();
    const direction = this.isArabic() ? 'to left' : 'to right';
    if (percentage < 50) return `linear-gradient(${direction}, #10b981, #059669)`;
    if (percentage <= 90) return `linear-gradient(${direction}, #f59e0b, #d97706)`;
    return `linear-gradient(${direction}, #dc2626, #b91c1c)`;
  });

  TotalCreditInK = computed(() => this.formatNumber(this.totalCredit()));
  UsedCreditInK = computed(() => this.formatNumber(this.usedCredit()));
  RemainingCreditInK = computed(() => this.formatNumber(this.remainingCredit()));

  constructor() {
    toObservable(this.pharmacyId)
      .pipe(
        filter((id): id is number => !!id),
        tap(() => this.apiError.set(null)),
        switchMap((id) => this.pharmacyCreditService.getPharmacyCredit(id)),
      )
      .subscribe({
        next: (res) => {
          this.apiError.set(null);
          this.totalCredit.set(res.totalCredit);
          this.usedCredit.set(res.usedCredit);
          this.remainingCredit.set(res.remainingCredit);
        },
        error: (err) => {
          this.totalCredit.set(null);
          this.usedCredit.set(null);
          this.remainingCredit.set(null);

          if (err?.status === 500) {
            this.apiError.set(this.isArabic() ? 'حدث خطأ ما' : 'Something went wrong');
          } else {
            const fallbackMsg = this.isArabic()
              ? 'فشل في تحميل بيانات الحد الائتماني'
              : 'Failed to load credit limit data';
            this.apiError.set(err?.error?.message || err?.message || fallbackMsg);
          }
        },
      });
  }

  private formatNumber(num: number | null): string {
    if (num == null) return '0';
    if (num >= 1000) {
      const value = num / 1000;
      const formatted = Number.isInteger(value) ? value.toString() : value.toFixed(1);
      const suffix = this.isArabic() ? ' ألف' : 'K';
      return formatted + suffix;
    }
    return num % 1 === 0 ? num.toString() : num.toFixed(1);
  }
}
